/*
  NoteStatusControl — spec §10 governance UI for a SOAP note.

  Displays the current status as a coloured pill and offers the legal
  next-transitions as buttons. Amendments (post-finalisation edits)
  and entered-in-error retractions require a modal prompt for a
  captured reason.

  Props
  -----
  note        : the current note object (needs at least id + status)
  onChange    : (updatedNote) => void  — called with the fresh server
                doc after any transition or amendment

  Actions when locked (finalised/amended):
    - "Amend" opens a dialog. Parent should populate `pendingEdits`
      via the `getPendingEdits()` prop-callback so the dialog knows
      what field changes are proposed. If none, the dialog is a no-op.
*/
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  FilePenLine, CheckCircle2, ShieldAlert, XCircle, Clock, PencilLine, History as HistoryIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

const STATUS_STYLES = {
  draft:            { label: "Draft",             tone: "bg-zinc-100 text-zinc-700 border-zinc-300",         icon: PencilLine },
  ready_for_review: { label: "Ready for review",  tone: "bg-amber-50 text-amber-800 border-amber-300",       icon: Clock },
  finalised:        { label: "Finalised",         tone: "bg-emerald-50 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
  amended:          { label: "Amended",           tone: "bg-sky-50 text-sky-800 border-sky-300",             icon: FilePenLine },
  entered_in_error: { label: "Entered in error",  tone: "bg-red-50 text-red-800 border-red-300 line-through",icon: XCircle },
};

export default function NoteStatusControl({ note, onChange, getPendingEdits, onOpenHistory }) {
  const [reasonDialog, setReasonDialog] = useState(null);  // "amend" | "error" | null
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const status = note?.status || "draft";
  const style = STATUS_STYLES[status] || STATUS_STYLES.draft;
  const StatusIcon = style.icon;

  async function transitionTo(next, extraReason = null) {
    setBusy(true);
    try {
      const r = await api.post(`/soap-notes/notes/${note.id}/status`, {
        status: next,
        reason: extraReason || undefined,
      });
      onChange?.(r.data);
      toast.success(`Marked as ${STATUS_STYLES[next]?.label || next}`);
    } catch (e) {
      toast.error("Couldn't change status", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  async function submitAmendment() {
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error("Reason must be at least 3 characters");
      return;
    }
    const edits = getPendingEdits?.() || {};
    if (!Object.keys(edits).length) {
      toast.error("Change at least one field before amending");
      setReasonDialog(null);
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/soap-notes/notes/${note.id}/amend`, {
        ...edits,
        reason: reason.trim(),
      });
      onChange?.(r.data);
      toast.success("Amendment recorded");
      setReasonDialog(null);
      setReason("");
    } catch (e) {
      toast.error("Amendment failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  async function submitEnterInError() {
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error("Reason required");
      return;
    }
    await transitionTo("entered_in_error", reason.trim());
    setReasonDialog(null);
    setReason("");
  }

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap" data-testid="note-status-control">
        <span
          className={cn(
            "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-2xs font-semibold uppercase tracking-wider",
            style.tone,
          )}
          data-testid={`note-status-${status}`}
        >
          <StatusIcon className="h-3 w-3" />
          {style.label}
        </span>

        {/* Actions vary by current state ------------------------------------ */}
        {status === "draft" && (
          <>
            <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => transitionTo("ready_for_review")}
                    data-testid="note-mark-ready">
              Send for review
            </Button>
            <Button size="sm" disabled={busy}
                    onClick={() => transitionTo("finalised")}
                    data-testid="note-finalise">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Finalise
            </Button>
          </>
        )}
        {status === "ready_for_review" && (
          <>
            <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => transitionTo("draft")}
                    data-testid="note-back-to-draft">
              Back to draft
            </Button>
            <Button size="sm" disabled={busy}
                    onClick={() => transitionTo("finalised")}
                    data-testid="note-finalise">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Finalise
            </Button>
          </>
        )}
        {(status === "finalised" || status === "amended") && (
          <>
            <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => { setReason(""); setReasonDialog("amend"); }}
                    data-testid="note-amend">
              <FilePenLine className="h-3.5 w-3.5 mr-1" /> Amend
            </Button>
            <Button size="sm" variant="outline" disabled={busy}
                    className="text-red-700 border-red-300 hover:bg-red-50"
                    onClick={() => { setReason(""); setReasonDialog("error"); }}
                    data-testid="note-mark-error">
              <ShieldAlert className="h-3.5 w-3.5 mr-1" /> Enter in error
            </Button>
          </>
        )}
        {onOpenHistory && (note?.amendments?.length > 0 || status !== "draft") && (
          <Button size="sm" variant="ghost" onClick={onOpenHistory}
                  className="text-zinc-600 hover:text-zinc-950"
                  data-testid="note-open-history">
            <HistoryIcon className="h-3.5 w-3.5 mr-1" /> History
          </Button>
        )}
      </div>

      {status === "entered_in_error" && note?.entered_in_error_reason && (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900" data-testid="note-error-banner">
          <span className="font-semibold uppercase tracking-wider text-2xs">Retracted — </span>
          {note.entered_in_error_reason}
          {note.entered_in_error_by_name && (
            <span className="text-red-700 ml-1">
              (by {note.entered_in_error_by_name})
            </span>
          )}
        </div>
      )}

      {/* Reason-capture dialog for amend / entered-in-error ------------- */}
      {reasonDialog && (
        <Dialog open onOpenChange={(v) => !v && setReasonDialog(null)}>
          <DialogContent data-testid="note-reason-dialog">
            <DialogHeader>
              <DialogTitle>
                {reasonDialog === "amend" ? "Amend finalised note" : "Mark note as entered in error"}
              </DialogTitle>
              <DialogDescription>
                {reasonDialog === "amend"
                  ? "The current version will be preserved in history. Your reason will appear on the amendment record."
                  : "This retracts the note but keeps it visible for audit. Cannot be undone."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label className="text-xs">Reason</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder={reasonDialog === "amend"
                  ? "e.g. Corrected side to right knee per client feedback"
                  : "e.g. Duplicate submission for the wrong client"}
                data-testid="note-reason-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReasonDialog(null)} disabled={busy}>Cancel</Button>
              <Button
                onClick={reasonDialog === "amend" ? submitAmendment : submitEnterInError}
                disabled={busy || reason.trim().length < 3}
                data-testid="note-reason-confirm"
              >
                {busy ? "Saving…" : (reasonDialog === "amend" ? "Save amendment" : "Retract note")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
